import { useEffect, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";

// side = who is looking: "customer" or "staff"; mine = messages sent by that side
export const ChatThread = ({ url, side, testId = "chat-thread", onRead }) => {
  const { t } = useI18n();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get(url).then((r) => {
      if (!alive) return;
      setMsgs(Array.isArray(r.data) ? r.data : r.data.messages);
      api.post(`${url}/read`).then(() => onRead && onRead()).catch(() => {});
    }).catch(() => alive && setMsgs([]));
    load();
    const id = setInterval(load, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [url, onRead]);

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [msgs?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(url, { text });
      setMsgs((m) => [...(m || []), data]);
      setText("");
    } catch (err) { toast.error(t("common.error")); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl bg-white shadow-sm" data-testid={testId}>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
        {msgs === null && <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />}
        {msgs?.length === 0 && <p className="py-10 text-center text-sm text-slate-500" data-testid="chat-empty">{t("x.chat.empty")}</p>}
        {msgs?.map((m) => {
          const mine = m.sender_type === side;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="chat-message">
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${mine ? "rounded-br-md bg-navy text-white" : "rounded-bl-md bg-canvas text-ink"}`}>
                <p className="whitespace-pre-line break-words">{m.text}</p>
                <p className={`mt-1 text-[10px] ${mine ? "text-white/60" : "text-slate-500"}`}>{m.sender_name} · {fmtDateTime(m.at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex items-end gap-2 border-t border-slate-100 p-3">
        <textarea rows={1} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("x.chat.placeholder")} data-testid="chat-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
          className="max-h-32 min-h-11 flex-1 resize-none rounded-2xl border border-slate-200 px-3 py-2.5 text-base text-ink outline-none focus:border-navy" />
        <button type="submit" disabled={busy || !text.trim()} aria-label={t("x.chat.send")} data-testid="chat-send"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand text-navy transition-transform active:scale-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};
